import * as _ from 'lodash';

import {
  BinaryInfo,
  EntityCheck,
  EntityMissing,
  FlowID,
  Mutation,
  NodeVar,
  NodeVarBinary,
  NodeVarFlow,
  NodeVarItem,
  NodeVarLeaderboard,
  NodeVarPackage,
  NodeVarPlatformDependentBinary,
  NotPromoted,
  OutdatedPromotion,
} from '@playq/octopus-common';
import { InvalidFlowReason } from '@playq/octopus2-liveops';
import { AppLadderId } from '@playq/services-clerk';
import { ItemID, PackageID } from '@playq/services-bookkeeper';
import { FileVersion, FileVersionOutdated } from '@playq/octopus2-files';

import { NodeErrors } from '/helpers/config/node';
import { services2 } from '/api/services2';
import { EntityNodes, NodeEntity, RemoteIds, TreeApiState } from '/shared/NewTree/types';
import { traverse } from '/shared/NewTree/helpers/traverse';
import { IDataCacheRecord, IExternalDataCacheState } from '/shared/Tree';

const getCheckedIdKey = (nodeId: string, entity: NodeVar | BinaryInfo) => {
  if (entity instanceof BinaryInfo) {
    return `binary-${entity.id}.${entity.ver}`;
  }
  if (entity instanceof NodeVarBinary) {
    return `binary-${entity.val.id}.${entity.val.ver}`;
  }
  if (entity instanceof NodeVarFlow || entity instanceof NodeVarItem || entity instanceof NodeVarPackage) {
    return entity.val.serialize();
  }
  if (entity instanceof NodeVarLeaderboard) {
    return entity.val;
  }
  return nodeId;
};

const cacheError = <T>(
  id: string,
  entity: T,
  externalDataCache: IExternalDataCacheState,
  selectEntityID?: (entity: T) => string
) => {
  const key = getCheckedIdKey(id, entity as NodeVar | BinaryInfo);
  externalDataCache.set(key, {
    data: selectEntityID?.(entity),
    error: NodeErrors.failedToLoadEntity,
  });
};

const processError = <T>(
  nodeEntity: NodeEntity<T>[],
  entityNodes: EntityNodes,
  error: unknown,
  selectEntityID?: (entity: T) => string,
  externalDataCache?: IExternalDataCacheState
) => {
  console.error(error);
  nodeEntity.forEach(({ id, entity }) => {
    if (externalDataCache !== undefined) {
      cacheError(id, entity, externalDataCache, selectEntityID);
    }
    entityNodes.set(id, {
      error: NodeErrors.failedToLoadEntity,
      id: selectEntityID?.(entity),
    });
  });
};

const processResponse = <Entity, EntityID>(
  responseEntities: Entity[],
  nodeEntities: NodeEntity<EntityID>[],
  entityNodes: EntityNodes,
  serializeId: (entityId: EntityID) => string,
  selectEntityId: (entity: Entity) => string,
  externalDataCache?: IExternalDataCacheState
) => {
  nodeEntities.forEach((nodeEntity) => {
    const entity = responseEntities.find((e) => serializeId(nodeEntity.entity) === selectEntityId(e));
    if (entity !== undefined) {
      entityNodes.set(nodeEntity.id, {
        error: NodeErrors.failedToLoadEntity,
        id: selectEntityId(entity),
      });
      if (externalDataCache !== undefined) {
        cacheError(nodeEntity.id, nodeEntity.entity, externalDataCache);
      }
    }
  });
};

const processVersionedPromotionEntities = <EntityID>(
  checks: {
    id: EntityID;
    status: EntityCheck;
  }[],
  nodes: {
    id: string;
    entityId: EntityID;
  }[],
  entityNodes: EntityNodes,
  serializeId: (entityId: EntityID) => string
) => {
  nodes.forEach((node) => {
    const check = checks.find((c) => serializeId(c.id) === serializeId(node.entityId));
    if (check === undefined) {
      return;
    }
    if (check.status instanceof EntityMissing) {
      return;
    }
    const isLiveNotPromoted = check.status.live instanceof NotPromoted;
    if (isLiveNotPromoted) {
      entityNodes.set(node.id, {
        error: NodeErrors.notPromoted,
        id: serializeId(node.entityId),
        errorDetails: 'Not promoted to live',
      });
      return;
    }
    const isLiveOutdatedPromotion = check.status.live instanceof OutdatedPromotion;
    const isSandboxNotPromoted = check.status.sandbox instanceof NotPromoted;
    const isSandboxOutdatedPromotion = check.status.sandbox instanceof OutdatedPromotion;
    const isWarning = isLiveOutdatedPromotion || isSandboxOutdatedPromotion || isSandboxNotPromoted;
    if (isWarning) {
      entityNodes.set(node.id, {
        warning: NodeErrors.promotedNotLastVersion,
        id: serializeId(node.entityId),
        errorDetails: isLiveOutdatedPromotion
          ? 'Promoted to live not the latest version'
          : isSandboxOutdatedPromotion
            ? 'Promoted to sandbox not the latest version'
            : 'Not promoted to sandbox',
      });
    }
  });
};

export const fetchCheckedRemoteEntities = async (
  ids: RemoteIds,
  fingerpintId?: string,
  externalDataCache?: IExternalDataCacheState
) => {
  const { binaryIds, flowIds, itemIds, packageIds, leaderboardIds } = ids;

  const entityNodes: EntityNodes = new Map();

  const promises: Promise<void>[] = [];

  if (binaryIds.length > 0) {
    const grouped = _.groupBy(binaryIds, (binaryInfo) => `${binaryInfo.entity.id}#${binaryInfo.entity.ver}`);
    promises.push(
      services2.filesService
        .checkFileVersions(
          Object.keys(grouped).map((key) => {
            const [fileId, ver] = key.split('#');
            const id = new FileVersion();
            id.version = +ver;
            id.id = +fileId;
            return id;
          }),
          false
        )
        .then((data) =>
          data.bifold(
            (res) => {
              return processResponse(
                res.versions.filter((v) => !(v.state instanceof FileVersionOutdated)),
                binaryIds,
                entityNodes,
                (binaryInfo) => binaryInfo.id.toString(),
                (entity) => entity.fileVersion.id.toString(),
                externalDataCache
              );
            },
            (err) => {
              throw err;
            }
          )
        )
        .catch((e) => processError(binaryIds, entityNodes, e, (en) => String(en.id), externalDataCache))
    );
  }

  if (flowIds.length > 0) {
    const grouped = _.groupBy(flowIds, (varFlow) => varFlow.entity.val.serialize());
    promises.push(
      services2.flowsService
        .checkFlows(
          Object.keys(grouped).map((key) => new FlowID(key)),
          false
        )
        .then((data) =>
          data.bifold(
            async (res) => {
              processResponse(
                Object.keys(res.flows).map((key) => new FlowID(key)),
                flowIds,
                entityNodes,
                (flow) => flow.val.id.toString(),
                (entity) => entity.id.toString(),
                externalDataCache
              );
              Object.keys(res.flows).forEach((key) => {
                const reason = res.flows[key];
                if (reason === InvalidFlowReason.Draft) {
                  const nodeWithFlow = flowIds.find((f) => f.entity.val.serialize() === key);
                  if (nodeWithFlow) {
                    entityNodes.set(nodeWithFlow.id, {
                      error: NodeErrors.draftFlow,
                      id: nodeWithFlow.entity.val.id.toString(),
                    });
                  }
                }
              });
            },
            (err) => {
              throw err;
            }
          )
        )
        .catch((e) => processError(flowIds, entityNodes, e))
    );
  }

  if (itemIds.length > 0) {
    const grouped = _.groupBy(itemIds, (varItem) => varItem.entity.val.serialize());
    promises.push(
      services2.appsInventoryService
        .checkItems(Object.keys(grouped).map((key) => new ItemID(key)))
        .then((data) =>
          data.bifold(
            (res) => {
              processResponse(
                res.checks.filter((c) => c.status instanceof EntityMissing),
                itemIds,
                entityNodes,
                (item) => item.val.serialize(),
                (entity) => entity.id.serialize(),
                externalDataCache
              );
              processVersionedPromotionEntities(
                res.checks,
                itemIds.map((item) => ({ id: item.id, entityId: item.entity.val })),
                entityNodes,
                (item) => item.id
              );
            },
            (err) => {
              throw err;
            }
          )
        )
        .catch((e) => processError(itemIds, entityNodes, e))
    );
  }

  if (leaderboardIds.length > 0 && fingerpintId) {
    const groupedEntityIds = _.groupBy(leaderboardIds, (entity) => entity.entity.val);
    promises.push(
      services2.lboardsService
        .checkLadders(
          Object.keys(groupedEntityIds).map((key) => {
            const appLadderId = new AppLadderId();
            appLadderId.appId = fingerpintId;
            appLadderId.ladderId = key;
            return appLadderId;
          })
        )
        .then((data) =>
          data.bifold(
            (res) => {
              processResponse(
                res.ladders,
                leaderboardIds,
                entityNodes,
                (lb) => lb.val,
                (entity) => entity.ladderId,
                externalDataCache
              );
            },
            (err) => {
              throw err;
            }
          )
        )
        .catch((e) => processError(leaderboardIds, entityNodes, e))
    );
  }

  if (packageIds.length > 0) {
    const grouped = _.groupBy(packageIds, (varPackage) => varPackage.entity.val.serialize());
    promises.push(
      services2.appsPackagesService
        .checkPackages(Object.keys(grouped).map((key) => new PackageID(key)))
        .then((data) =>
          data.bifold(
            (res) => {
              processResponse(
                res.checks.filter((c) => c.status instanceof EntityMissing),
                packageIds,
                entityNodes,
                (pkg) => pkg.val.serialize(),
                (entity) => entity.id.serialize(),
                externalDataCache
              );
              processVersionedPromotionEntities(
                res.checks,
                packageIds.map((item) => ({ id: item.id, entityId: item.entity.val })),
                entityNodes,
                (item) => item.id
              );
            },
            (err) => {
              throw err;
            }
          )
        )
        .catch((e) => processError(packageIds, entityNodes, e))
    );
  }

  return Promise.all(promises).then(() => entityNodes);
};

export const checkRemoteNodeVar = (
  nodeId: string,
  nodeVar: NodeVar,
  externalDataCache?: IExternalDataCacheState
): { ids: RemoteIds; entityNodes: EntityNodes } => {
  const entityNodes: EntityNodes = new Map();
  const ids: RemoteIds = {
    binaryIds: [],
    flowIds: [],
    itemIds: [],
    leaderboardIds: [],
    packageIds: [],
  };

  const checkedEntityKey = getCheckedIdKey(nodeId, nodeVar);

  if (externalDataCache?.has(checkedEntityKey)) {
    const cached = externalDataCache.get(checkedEntityKey) as IDataCacheRecord;
    if (cached.error !== undefined) {
      entityNodes.set(nodeId, {
        id: cached.data as string | number,
        error: cached.error,
      });
    }
    return { ids, entityNodes };
  }

  if (nodeVar instanceof NodeVarBinary) {
    const checkedIdKey = getCheckedIdKey(nodeId, nodeVar.val);
    if (!externalDataCache?.has(checkedIdKey)) {
      const entity = {
        id: nodeId,
        entity: nodeVar.val,
      };
      ids.binaryIds.push(entity);
      externalDataCache?.set(checkedIdKey, { data: entity });
    }
  } else if (nodeVar instanceof NodeVarFlow) {
    ids.flowIds.push({
      id: nodeId,
      entity: nodeVar,
    });
  } else if (nodeVar instanceof NodeVarItem) {
    ids.itemIds.push({
      id: nodeId,
      entity: nodeVar,
    });
  } else if (nodeVar instanceof NodeVarLeaderboard) {
    ids.leaderboardIds.push({ id: nodeId, entity: nodeVar });
  } else if (nodeVar instanceof NodeVarPackage) {
    ids.packageIds.push({
      id: nodeId,
      entity: nodeVar,
    });
  } else if (nodeVar instanceof NodeVarPlatformDependentBinary) {
    const { binaries } = nodeVar;
    for (const key of Object.keys(binaries)) {
      const binary = binaries[key];
      const checkedIdKey = getCheckedIdKey(nodeId, binary);
      if (!externalDataCache?.has(checkedIdKey)) {
        const entity = {
          id: nodeId,
          entity: binary,
        };
        ids.binaryIds.push(entity);
        externalDataCache?.set(checkedIdKey, { data: entity });
      }
    }
  }

  externalDataCache?.set(checkedEntityKey, { data: nodeVar });

  return {
    ids,
    entityNodes,
  };
};

export const fetchRemoteEntities = async (
  cb: (checkNodeVar: (nodeId: string, nodeVar: NodeVar) => void) => void,
  fingerpintId?: string,
  externalDataCache?: IExternalDataCacheState
): Promise<EntityNodes> => {
  const entityNodes: EntityNodes = new Map();
  const binaryIds: NodeEntity<BinaryInfo>[] = [];
  const flowIds: NodeEntity<NodeVarFlow>[] = [];
  const itemIds: NodeEntity<NodeVarItem>[] = [];
  const leaderboardIds: NodeEntity<NodeVarLeaderboard>[] = [];
  const packageIds: NodeEntity<NodeVarPackage>[] = [];

  const checkNodeVar = (nodeId: string, nodeVar: NodeVar) => {
    const { ids, entityNodes: updatedEntityNodes } = checkRemoteNodeVar(nodeId, nodeVar, externalDataCache);

    updatedEntityNodes.forEach((value, key) => entityNodes.set(key, value));
    binaryIds.push(...ids.binaryIds);
    flowIds.push(...ids.flowIds);
    itemIds.push(...ids.itemIds);
    leaderboardIds.push(...ids.leaderboardIds);
    packageIds.push(...ids.packageIds);
  };

  cb(checkNodeVar);

  const checked = await fetchCheckedRemoteEntities(
    { binaryIds, flowIds, itemIds, leaderboardIds, packageIds },
    fingerpintId
  );
  checked.forEach((value, key) => entityNodes.set(key, value));

  return entityNodes;
};

export const fetchTreeRemoteEntities = (
  nodes: TreeApiState['nodes'],
  rootNodes: TreeApiState['rootNodes'],
  fingerpintId?: string,
  externalDataCache?: IExternalDataCacheState
) => {
  return fetchRemoteEntities(
    (checkNodeVar) => {
      traverse(nodes, rootNodes, (id) => {
        const node = nodes.get(id);
        if (node?.type === 'value') {
          checkNodeVar(id, node.var);
        } else if (node?.type === 'array') {
          node.children.forEach((nodeVar) => {
            checkNodeVar(id, nodeVar);
          });
        }
      });
    },
    fingerpintId,
    externalDataCache
  );
};

export const fetchMutationRemoteEntities = (mutations: Mutation[], fingerpintId?: string) =>
  fetchRemoteEntities((checkNodeVar) => {
    for (const mutation of mutations) {
      for (const segment of mutation.segments) {
        for (const segmentValue of segment.values) {
          checkNodeVar(`${mutation.id}.${segment.id}.${segmentValue.node}.${segmentValue.path}`, segmentValue.var_);
        }
      }
    }
  }, fingerpintId);
